import { Box } from '@chakra-ui/react';
import { useDndContext } from '@dnd-kit/core';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { match } from 'ts-pattern';

import { BrandExperience } from '../blocks/BrandExperience';
import { TalentContent } from '../blocks/TalentContent/TalentContent';
import { TalentContentReadonly } from '../blocks/TalentContent/TalentContentReadonly';
import { TalentProfile } from '../blocks/TalentProfile';
import { TalentVideo } from '../blocks/TalentVideo';
import { usePageBuilderStore } from '../hooks/usePageBuilderStore';
import { AnyBlock } from '../types';

const TalentCharts = dynamic(
  () =>
    import('../blocks/TalentCharts/TalentCharts').then((c) => c.TalentCharts),
  {
    loading: () => null,
  },
);

const EditableRichTextView = dynamic(
  () =>
    import('../blocks/EditableRichTextView').then(
      (c) => c.EditableRichTextView,
    ),
  {
    loading: () => null,
  },
);

export const ComponentRenderer = ({
  block,
  readonly,
  isDragging,
}: {
  block: AnyBlock;
  readonly?: boolean; // Default to false for PageBuilder
  isDragging?: boolean;
}) => {
  const { setEditingBlockId } = usePageBuilderStore();

  const router = useRouter();
  const id = router.query.id as string;

  // Detect if we're on a shared page
  const isShared = router.pathname.includes('/shared/');

  return (
    <>
      {match(block)
        .with({ component: 'TalentProfile' }, (block) => (
          <StyledBlockSection blockId={block.id} isDragging={isDragging}>
            <TalentProfile
              creatorId={block.properties.creatorId}
              mediaKitId={id}
            />
          </StyledBlockSection>
        ))
        .with({ component: 'TalentContent' }, (block) => {
          return (
            <StyledBlockSection blockId={block.id} isDragging={isDragging}>
              {readonly ? (
                <TalentContentReadonly {...block.properties} />
              ) : (
                <TalentContent {...block.properties} blockId={block.id} />
              )}
            </StyledBlockSection>
          );
        })
        .with({ component: 'TalentCharts' }, (block) => (
          <StyledBlockSection blockId={block.id} isDragging={isDragging}>
            <TalentCharts {...block.properties} />
          </StyledBlockSection>
        ))
        .with({ component: 'EditableRichTextView' }, (block) => (
          <StyledBlockSection
            blockId={block.id}
            isDragging={isDragging}
            onClick={() => (readonly ? '' : setEditingBlockId(block.id))}
          >
            <EditableRichTextView
              {...block.properties}
              id={block.id}
              readonly={readonly}
            />
          </StyledBlockSection>
        ))
        .with({ component: 'TalentVideo' }, (block) => (
          <StyledBlockSection blockId={block.id} isDragging={isDragging}>
            <TalentVideo {...block.properties} />
          </StyledBlockSection>
        ))
        .with({ component: 'BrandLogos' }, (block) => (
          <StyledBlockSection blockId={block.id} isDragging={isDragging}>
            <BrandExperience
              items={(block.properties?.logos || []).map((logo) => ({
                src: logo.url,
                alt: logo.brandName,
              }))}
              isShared={isShared}
            />
          </StyledBlockSection>
        ))

        .otherwise(() => null)}
    </>
  );
};

const StyledBlockSection = ({
  children,
  isDragging,
  blockId,
  onClick,
}: {
  children: React.ReactNode;
  isDragging?: boolean;
  blockId: string;
  onClick?: () => void;
}) => {
  const router = useRouter();
  const dndContext = useDndContext();
  const isPreview = router.pathname.includes('/shared/');
  const isAnyItemDragging = Boolean(dndContext.active?.id);

  return (
    <Box
      _groupHover={{
        background: 'var(--media-kit-widget-background-hover)',
      }}
      padding={{
        base: 0,
        lg: isPreview ? 0 : 6, // No padding on shared pages
      }}
      border="1px solid var(--media-kit-background-primary)"
      borderColor={
        isDragging ? 'var(--media-kit-background-alpha)!important' : ''
      }
      background={
        isDragging ? 'var(--media-kit-widget-background-drag)!important' : ''
      }
      rounded="1.25rem"
      transition="all 0.2s ease-in-out"
      opacity={isDragging ? 0.75 : 1}
      data-block-id={blockId}
      onClick={onClick}
      pointerEvents={isAnyItemDragging ? 'none' : 'auto'}
    >
      {children}
    </Box>
  );
};
